import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { Filter } from '../../../../list/interfaces/list-filter/filter';
import { COMMON_REFRESH_KEY } from '../../../../a11y/shortcuts/constants/constants';
import styles from './styles.module.scss';
import HdIcon from '../../../../media/components/icon/HdIcon';
import { HdIconButton } from '../../../../components';
import HdShortcut from '../../../../a11y/shortcuts/components/shortcut/HdShortcut';
import { HdTooltip } from '../../../../components';
import ActivityLogAPI from '../../ActivityLogAPI';
import { HdSpinner } from '../../../../loader/components/spinner/HdSpinner';
import { FilterMenu } from './FiltersMenu';
import { AppliedFilters } from './AppliedFilters';
import useKeyboardShortcutListener from '../../../../a11y/shortcuts/hooks/useKeyboardShortcutListener';

interface ActivityLogInlineFiltersProps {
  filters: Filter<any>[];
  loading: boolean;
  appliedFilters: Array<{ name: string; value: { name: string } }>;
  resetFilter: Function;
  updateFilter: Function;
  onRefreshCallback?: Function;
  routerPush?: Function;
}

export default function ActivityLogInlineFilters({
  filters,
  loading,
  appliedFilters,
  resetFilter,
  updateFilter,
  onRefreshCallback,
  routerPush
}: ActivityLogInlineFiltersProps) {
  const [cloudwatchSyncEnabled, setCloudwatchSyncEnabled] = useState(false);
  const [cloudwatchSyncLoading, setCloudwatchSyncLoading] = useState(false);
  const [cloudwatchSyncLoadError, setCloudwatchSyncLoadError] = useState(undefined);

  const defaultStatus = !!(cloudwatchSyncLoading || cloudwatchSyncLoadError);
  const showStatus = !cloudwatchSyncLoading && !cloudwatchSyncLoadError;
  const showFilters = !cloudwatchSyncLoading && filters.length;
  const showEnabledIcon =
    !cloudwatchSyncLoading && !cloudwatchSyncLoadError && cloudwatchSyncEnabled;

  useKeyboardShortcutListener(onRefreshCallback, COMMON_REFRESH_KEY, {
    priority: 0,
    terminal: 'match'
  });

  useEffect(() => {
    getCloudwatchSyncConfig();
  }, []);

  const getCloudwatchSyncConfig = async () => {
    setCloudwatchSyncLoading(true);

    try {
      const { data } = await ActivityLogAPI.getClientLogger();

      setCloudwatchSyncLoading(false);
      setCloudwatchSyncEnabled(!!data.id);
    } catch (error) {
      setCloudwatchSyncLoading(false);

      if (error.status === 404) {
        setCloudwatchSyncEnabled(false);
      } else {
        setCloudwatchSyncLoadError(error);
      }
    }
  };

  return (
    <div className={styles.filtersContainer}>
      {showFilters ? (
        <div className={styles.appliedFiltersContainer}>
          <div className={styles.filterWrapper}>
            <div className={styles.appliedFilter}>
              <AppliedFilters appliedFilters={appliedFilters} resetFilter={resetFilter} />
            </div>

            <div className={styles.cloudwatchStatus}>
              {cloudwatchSyncLoading ? <HdSpinner classes='mr-1' variant='primary' /> : null}

              {showEnabledIcon ? (
                <HdIcon name='check-circle' className='mr-1 text-success' />
              ) : null}

              <Typography variant='body2'>{`CloudWatch Sync `}</Typography>

              <Typography variant='body2'>
                {defaultStatus ? 'Status' : null}

                {/* eslint-disable-next-line no-nested-ternary */}
                {showStatus ? (cloudwatchSyncEnabled ? 'Enabled' : 'Disabled') : null}
              </Typography>

              <HdIconButton
                className={`ml-1 ${styles.editIcon}`}
                onClick={() => routerPush('/team/cloudwatch')}
                dataId='activity-log-page-edit'
              >
                <HdIcon name='edit' />
              </HdIconButton>
            </div>

            <div className='filter-separator' />

            <FilterMenu filters={filters} updateFilter={updateFilter} />

            <div className='filter-separator' />

            <HdTooltip title={<HdShortcut command={COMMON_REFRESH_KEY} />}>
              <HdIconButton
                onClick={() => onRefreshCallback()}
                className={styles.refreshButton}
                dataId='activity-log-page-refresh'
              >
                <HdIcon
                  name='refresh'
                  size={3}
                  className={`refresh-icon ${loading ? 'refreshing' : ''}`}
                />
              </HdIconButton>
            </HdTooltip>
          </div>
        </div>
      ) : (
        <>
          <div className={`shimmer shimmer-line my-2 ${styles.shimmerBig}`} />

          <div className={`shimmer shimmer-line my-2 ${styles.shimmerSmall}`} />
        </>
      )}
    </div>
  );
}
